import * as React from "react";
import Link from "next/link";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
} from "@reach/accordion";
import { Radio, RadioGroup } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { Button, Heading, Markdown, Separator, Text } from "@nju/ui";
import { A } from "@nju/ui";
import { IContext } from "../changeConsents.machine";

type ISelectConsent = ({
  value,
  consentId,
  consentVersion,
}: {
  value: string;
  consentId: string;
  consentVersion: number;
}) => void;

interface IChangeConsentsFormProps {
  context: IContext;
  selectConsent: ISelectConsent;
  handleSubmit: () => void;
  isSaving: boolean;
}

export function ChangeConsentsForm({
  context,
  selectConsent,
  handleSubmit,
  isSaving,
}: IChangeConsentsFormProps) {
  const [openAccordions, setOpenAccordions] = React.useState<
    Record<string, boolean>
  >({});

  const toggleAccordion = (id: string) => {
    setOpenAccordions((prev) => {
      return {
        ...prev,
        [id]: !prev[id],
      };
    });
  };

  function getConsentRadioValue(consentId: string) {
    const userConsent = context.userConsents.find((c) => {
      return c.consentId === consentId;
    });

    if (!userConsent || userConsent.accepted === undefined) {
      return;
    }
    if (userConsent.accepted) {
      return "yes";
    }
    return "no";
  }

  return (
    <>
      <div className="flex gap-3 items-center mb-3">
        <Icon height={32} name="check-double" width={32} />
        <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
          zgody
        </Heading>
      </div>
      <Separator size="none" />
      <div className="grid grid-flow-col gap-3 mb-5 mt-5">
        <Icon height={24} name="info-circle" tone="accent" width={24} />
        <Text size="s">
          Poniższe zgody są dobrowolne. Brak zgód nie wpłynie negatywnie na
          warunki świadczenia przez nas usług. W niektórych ofertach udzielenie
          zgody wiąże się jednak z rabatem na abonament za świadczone usługi.
          Zgody są ważne dopóki jesteś naszym Klientem, chyba że je wcześniej w
          dowolnej chwili wycofasz. Nie będzie to miało wpływu na zgodność z
          prawem wykorzystania Twoich danych przed cofnięciem zgody. Jeśli
          wycofasz którąś ze zgód udzielonych tutaj, automatycznie nie uznajemy,
          że wycofujesz analogiczne zgody w ofercie Orange Flex. Jeśli chcesz
          odwołać swoje zgody w Orange Flex, wejdź na swoje konto w aplikacji.
        </Text>
      </div>
      {context?.consentsCatalogue?.map(
        ({ consentText, consentLongText, id, consentVersion }) => {
          return (
            <React.Fragment key={id}>
              <Separator size="none" />
              <Text className="mt-5" display="block" size="l" weight="bold">
                {consentText}
              </Text>
              <Accordion
                collapsible={true}
                onChange={() => {
                  return toggleAccordion(id);
                }}
              >
                <AccordionItem>
                  <AccordionPanel className="mt-1">
                    <Markdown className="prose-sm" content={consentLongText} />
                  </AccordionPanel>
                  <AccordionButton className="flex items-center cursor-pointer bg-transparent border-none mt-1 ml-auto">
                    <Text size="s" weight="bold">
                      {openAccordions[id] ? "zwiń" : "rozwiń"}
                    </Text>
                    <Icon
                      className="w-[13px] h-[14px] ml-2"
                      name={openAccordions[id] ? "chevron-up" : "chevron-down"}
                      tone="neutral"
                    />
                  </AccordionButton>
                </AccordionItem>
              </Accordion>
              <div className="mt-5 mb-1">
                <RadioGroup
                  aria-label={id}
                  name={id}
                  onValueChange={(value) => {
                    return selectConsent({
                      value,
                      consentId: id,
                      consentVersion,
                    });
                  }}
                  value={getConsentRadioValue(id)}
                >
                  <div className="flex w-full">
                    <div className="flex-1">
                      <Radio value="yes">
                        <Text size="s">wyrażam zgodę</Text>
                      </Radio>
                    </div>
                    <div className="flex-1">
                      <Radio value="no">
                        <Text size="s">nie wyrażam zgody</Text>
                      </Radio>
                    </div>
                    <div className="flex-1" />
                  </div>
                </RadioGroup>
              </div>
            </React.Fragment>
          );
        },
      )}

      <Separator size="none" />
      <div className="flex only-mobile:flex-col-reverse from-tablet:flex-row from-tablet:justify-start mt-5">
        <Link
          className="only-mobile:mt-3 from-tablet:mr-4"
          href={portalRoutes.mojeKonto.mojProfil.index.get()}
          passHref={true}
        >
          <A
            size={{ "@onlyMobile": "s", "@fromTablet": "m" }}
            variant="secondary"
          >
            anuluj
          </A>
        </Link>
        <Button
          isLoading={isSaving}
          onPress={handleSubmit}
          size={{ "@onlyMobile": "s", "@fromTablet": "m" }}
          testId="save-consents"
        >
          zapisz zmiany
        </Button>
      </div>
    </>
  );
}
